import React from 'react';
import { makeStyles } from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4.0;
const ZOOM_STEP = 0.25;

export function stepZoom(zoom: number, direction: 1 | -1): number {
    const next = parseFloat((zoom + direction * ZOOM_STEP).toFixed(2));
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    group: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
        backgroundColor: 'white',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    fitButton: {
        height: 32,
        padding: '0 14px',
        border: 'none',
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        background: 'white',
        cursor: 'pointer',
        fontSize: 13,
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
    },
    scaleReadout: {
        height: 32,
        lineHeight: '30px',
        padding: '0 14px',
        boxSizing: 'border-box' as const,
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        backgroundColor: 'white',
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.primary.main,
        whiteSpace: 'nowrap' as const,
    },
});

interface ZoomControlsProps {
    zoom: number;
    onZoomChange: (zoom: number) => void;
    pxPerMeter: number;
}

export default function ZoomControls({ zoom, onZoomChange, pxPerMeter }: ZoomControlsProps) {
    const classes = useStyles();
    return (
        <div className={classes.root}>
            <div className={classes.group}>
                <button
                    className={classes.zoomButton}
                    onClick={() => onZoomChange(stepZoom(zoom, -1))}
                    disabled={zoom <= ZOOM_MIN}
                    type="button"
                    aria-label={i18n.t('tableMaps.wizard.draw.zoomOut')}
                >
                    −
                </button>
                <button className={classes.fitButton} onClick={() => onZoomChange(1)} type="button">
                    {i18n.t('tableMaps.wizard.draw.fitToRoom')}
                </button>
                <button
                    className={classes.zoomButton}
                    onClick={() => onZoomChange(stepZoom(zoom, 1))}
                    disabled={zoom >= ZOOM_MAX}
                    type="button"
                    aria-label={i18n.t('tableMaps.wizard.draw.zoomIn')}
                >
                    +
                </button>
            </div>
            <span className={classes.scaleReadout}>
                {i18n.t('tableMaps.wizard.draw.pxPerMeter', {
                    value: Math.round(pxPerMeter * zoom),
                })}
            </span>
        </div>
    );
}
